"use client";

import { StrictMode, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { Box, ChakraProvider, Heading, Text, VStack } from "@chakra-ui/react";
import { useAppTheme, ThemeProvider } from "./ThemeContext.tsx";

const faviconUrl = process.env.REACT_APP_TAB_ICON_URL;
if (faviconUrl) {
  const favicon = document.getElementById("favicon");
  if (favicon) {
    favicon.href = faviconUrl;
  }
}

const SubscriptionExpiredContent = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <ChakraProvider>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bg="gray.100"
      >
        <VStack
          spacing={6}
          p={8}
          bg="white"
          borderRadius="md"
          boxShadow="lg"
          maxW="md"
          textAlign="center"
        >
          <Heading size="lg" color="red.500">
            Subscription Expired
          </Heading>
          <Text fontSize="md" color={isDark ? "gray.200" : "gray.700"}>
            Your subscription has expired. Please renew your subscription to
            continue using the application.
          </Text>
        </VStack>
      </Box>
    </ChakraProvider>
  );
};

const SubscriptionExpired = () => {
  return (
    <ThemeProvider>
      <SubscriptionExpiredContent />
    </ThemeProvider>
  );
};

const AppLoader = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    const checkAppStatus = async () => {
      try {
        const domainName = process.env.REACT_APP_DOMAIN_NAME;
        if (!domainName) {
          setIsActive(true);
          return;
        }
        const response = await fetch(
          `/api/is-app-active?${new URLSearchParams({ app_domain: domainName })}`,
        );

        if (response.ok) {
          const data = await response.json();
          setIsActive(data.is_active);
        } else {
          console.error("Failed to check app status:", response.statusText);
          // Default to active if we can't check
          setIsActive(true);
        }
      } catch (error) {
        console.error("Error checking app status:", error);
        // Default to active if we can't check
        setIsActive(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAppStatus();
  }, []);

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return isActive ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <SubscriptionExpired />
  );
};

createRoot(document.getElementById("root")!).render(<AppLoader />);
